import React from 'react';
import { useTranslation } from 'react-i18next';
import { useClientApi } from '@hero/api-hooks';
import { BuildingIcon } from '@hero/web-icons';
import type { Client, Id } from '@hero/schema';
import { cn } from '@hero/web-lib';

export interface ClientLinkingBannerProps {
  /**
   * Pre-fetched client. Pass either this or {@link clientId}; when
   * both are provided, `client` wins and no fetch is performed.
   */
  client?: Client;
  /**
   * Client id — the banner fetches the client itself via
   * {@link useClientApi}. Useful when the caller only knows the id
   * (e.g. a modal pre-seeded from a URL param).
   */
  clientId?: Id;
  /**
   * Override the "Linking to client" caption. Defaults to
   * `m-client:linking_banner.title`. Pass a different string when
   * the surrounding flow isn't a "linking" verb (e.g. "Adding under").
   */
  caption?: string;
  className?: string;
}

/**
 * Compact banner that surfaces the target client at the top of a
 * scoped flow (e.g. the "create contact / place / agreement" modals
 * opened from the Client workspace). Renders the client name plus a
 * muted role list — intentionally subtle so it sits above the form
 * without competing with the inputs for attention. Replaces the
 * earlier ad-hoc copy of this banner that lived inside
 * `ContactEditModal` and reused the colored `ClientRolesBadge`,
 * which clashed with the gray frame.
 *
 * Returns `null` while the client is still loading or when neither
 * `client` nor a resolved `clientId` is available, so consumers can
 * unconditionally drop the banner into their layout.
 */
export function ClientLinkingBanner({
  client,
  clientId,
  caption,
  className,
}: ClientLinkingBannerProps) {
  const { t } = useTranslation(['m-client']);

  // Skip the network round-trip when the consumer already has the
  // full client doc in hand — the workspace tabs always do, modals
  // opened from a deep-link only know the id.
  const fetched = useClientApi(client ? undefined : clientId);
  const resolved = client ?? fetched.data;

  if (!resolved) return null;

  const captionText =
    caption ?? t('m-client:linking_banner.title', { defaultValue: 'Linking to client' });

  const roles = Array.isArray(resolved.roles) ? resolved.roles : [];
  const rolesLabel = roles
    .map(role => t(`m-client:roles.${role}.title`, { defaultValue: role }))
    .join(' · ');

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5',
        className,
      )}>
      <div className="flex items-center justify-center rounded-md bg-white border p-1.5 flex-shrink-0">
        <BuildingIcon className="size-3.5 text-gray-400" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs font-medium leading-none text-muted-foreground">
          {captionText}
        </p>
        <p className="text-sm font-semibold leading-tight text-gray-900 truncate">
          {resolved.name}
        </p>
        {rolesLabel ? (
          <p className="text-xs leading-tight text-muted-foreground truncate">
            {rolesLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
}
